import React, { useState } from "react";

import { Avatar, type AvatarPerson } from "../Avatar";
import { ConfirmByTypingModal } from "../ConfirmByTypingModal";
import {
  IconFileExport,
  IconFileText,
  IconLetterCase,
  IconLock,
  IconShieldLock,
  IconTrash,
  IconUser,
  IconUsers,
} from "../icons";
import { Link } from "../Link";
import { OptionsMenuItem } from "../OptionsMenuItem";
import { Page } from "../Page";
import { PageSection } from "../PageSection";

export namespace CompanyAdminPage {
  export type Person = AvatarPerson;

  export interface Props {
    companyName: string;
    admins: Person[];
    owners: Person[];

    isAdmin: boolean;
    isOwner: boolean;
    billingEnabled: boolean;
    canManageBilling: boolean;
    canEditDetails: boolean;
    canEditTrustedEmailDomains: boolean;

    homePath: string;
    permissionsPath: string;
    managePeoplePath: string;
    restoreSuspendedPeoplePath: string;
    billingPath: string;
    renameCompanyPath: string;
    manageAdminsPath: string;
    trustedDomainsPath: string;
    exportPath: string;

    onDeleteCompany: () => Promise<void>;
  }
}

export function CompanyAdminPage(props: CompanyAdminPage.Props) {
  const navigation = React.useMemo(() => [{ to: props.homePath, label: "Главная" }], [props.homePath]);

  return (
    <Page
      title={[props.companyName, "Administration"]}
      size="small"
      testId="company-admin-page"
      navigation={navigation}
    >
      <div className="px-10 py-8">
        <div className="uppercase text-sm tracking-wide">Администрирование компании</div>
        <div className="text-content-accent text-3xl font-extrabold">{props.companyName}</div>

        <PageSection title="Что это?">
          <p>
            This is the company administration page where owners and admins can manage the company's settings. They have
            special permissions to add or remove team members, change who can access the application, and more. If you
            need something done, contact one of them.
          </p>

          <p className="mt-2">
            <Link to={props.permissionsPath}>Посмотреть разбивку разрешений</Link>
          </p>
        </PageSection>

        {props.admins.length > 0 && (
          <PageSection title="Администраторы">
            <PeopleList people={props.admins} />
          </PageSection>
        )}

        {props.owners.length > 0 && (
          <PageSection title="Владельцы аккаунт">
            <PeopleList people={props.owners} />
          </PageSection>
        )}

        <AdminsMenu {...props} />
        <OwnersMenu {...props} />
        <DangerZone
          isOwner={props.isOwner}
          companyName={props.companyName}
          onDeleteCompany={props.onDeleteCompany}
        />
      </div>
    </Page>
  );
}

function AdminsMenu(props: CompanyAdminPage.Props) {
  if (!(props.isAdmin || props.isOwner)) {
    return null;
  }

  return (
    <PageSection title="Как администратор или владелец ты можешь:">
      <div>
        <OptionsMenuItem linkTo={props.managePeoplePath} icon={IconUsers} title="Управлять участниками команды" />
        <OptionsMenuItem
          linkTo={props.restoreSuspendedPeoplePath}
          icon={IconUser}
          title="Восстановить доступ для деактивированных участников команды"
        />
        <OptionsMenuItem
          hidden={!props.billingEnabled || !props.canManageBilling}
          linkTo={props.billingPath}
          icon={IconFileText}
          title="Управлять тарифом"
        />
        <OptionsMenuItem
          hidden={!props.canEditDetails}
          linkTo={props.renameCompanyPath}
          icon={IconLetterCase}
          title="Переименовать компанию"
        />
      </div>
    </PageSection>
  );
}

function OwnersMenu(props: CompanyAdminPage.Props) {
  if (!props.isOwner) {
    return null;
  }

  return (
    <PageSection title="Как владелец ты можешь:">
      <div>
        <OptionsMenuItem
          linkTo={props.manageAdminsPath}
          icon={IconShieldLock}
          title="Управлять администраторами и владельцами"
        />
        <OptionsMenuItem
          hidden={!props.canEditTrustedEmailDomains}
          linkTo={props.trustedDomainsPath}
          icon={IconLock}
          title="Управлять доверенными доменами почты"
        />
        <OptionsMenuItem linkTo={props.exportPath} icon={IconFileExport} title="Экспортировать данные компании" />
      </div>
    </PageSection>
  );
}

function PeopleList({ people }: { people: AvatarPerson[] }) {
  return (
    <div className="flex flex-wrap gap-4">
      {people.map((person) => (
        <div key={person.id ?? person.fullName} className="flex items-center gap-2">
          <Avatar person={person} size="small" />
          <div className="font-medium">{person.fullName}</div>
        </div>
      ))}
    </div>
  );
}

function DangerZone({
  isOwner,
  companyName,
  onDeleteCompany,
}: {
  isOwner: boolean;
  companyName: string;
  onDeleteCompany: () => Promise<void>;
}) {
  if (!isOwner) return null;

  return (
    <PageSection title="Опасная зона:">
      <div className="bg-surface-base">
        <DeleteCompanyItem companyName={companyName} onDeleteCompany={onDeleteCompany} />
      </div>
    </PageSection>
  );
}

function DeleteCompanyItem({
  companyName,
  onDeleteCompany,
}: {
  companyName: string;
  onDeleteCompany: () => Promise<void>;
}) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <OptionsMenuItem
        icon={IconTrash}
        title="Удалить эту компанию"
        onClick={() => setShowModal(true)}
        danger
        description="Удалить компанию и все её ресурсы без возможности восстановления. Это действие нельзя отменить."
      />

      <ConfirmByTypingModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={onDeleteCompany}
        title="Удалить компанию"
        confirmationValue={companyName}
        warningMessage="This action cannot be undone."
        warningDescription={
          <>Это навсегда удалит<strong>{companyName}</strong> and its spaces, goals, projects, and other
            resources.
          </>
        }
        confirmLabel="Delete Company"
        loadingLabel="Deleting..."
        inputTestId="confirm-delete-input"
        confirmTestId="confirm-delete-button"
      />
    </>
  );
}
